import {
  ENTITY_CLASS,
  type QueryOptions,
  Watchtower,
  WatchtowerKey,
  type WatchtowerQueryFilters,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { fetchData } from "./functions";
import { QueryKey } from "./query";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findWatchtower(watchtowerKey: WatchtowerKey | false | null | undefined) {
  if (!watchtowerKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Watchtower", input: { watchtowerKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function WatchtowerQueryKey(watchtowerKey: WatchtowerKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.WATCHTOWER, watchtowerKey);
}

export function updateWatchtowerCache(
  watchtowerKey: WatchtowerKey | false | null | undefined,
  watchtower?:
    Watchtower | { (watchtower: Watchtower | null | undefined): Watchtower | null | undefined },
  options?: Partial<{
    invalidate: boolean;
    remove: boolean;
  }>,
) {
  if (watchtowerKey) {
    const queryKey = WatchtowerQueryKey(watchtowerKey);
    if (watchtower) {
      TANSTACK_QUERY_CLIENT.setQueryData(queryKey, watchtower);
    }
    if (options?.invalidate) {
      TANSTACK_QUERY_CLIENT.invalidateQueries({ exact: true, queryKey });
    }
    if (options?.remove) {
      TANSTACK_QUERY_CLIENT.invalidateQueries({ exact: true, queryKey });
    }
  }
}

export function useWatchtower(
  watchtowerKey: WatchtowerKey | false | null | undefined,
  options?: {
    /** 仮想化された一覧で表示される場合に`true`を設定するフラグ */
    virtualized?: boolean;
  },
) {
  const { data: watchtower } = useQuery({
    queryKey: WatchtowerQueryKey(watchtowerKey),
    async queryFn() {
      return findWatchtower(watchtowerKey);
    },

    enabled: Boolean(watchtowerKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: !options?.virtualized,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return watchtower;
}

export async function listWatchtowers(filters?: WatchtowerQueryFilters, options?: QueryOptions) {
  const { output } = await fetchData({
    type: "Entities::Watchtowers",
    input: { filters, options },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function WatchtowersQueryKey(...[filters, options]: Parameters<typeof useListedWatchtowers>) {
  return QueryKey(ENTITY_CLASS.WATCHTOWER + "List", filters, options);
}

export function removeWatchtowersCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: WatchtowersQueryKey().slice(0, 1) });
}

export function useListedWatchtowers(
  filters?: WatchtowerQueryFilters,
  options?: QueryOptions & {
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled?: boolean;
  },
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: WatchtowersQueryKey(filters, options),
    async queryFn(context) {
      const { watchtowers, nextPageToken } = await listWatchtowers(filters, {
        ...options,
        nextPageToken: context.pageParam.nextPageToken,
      });

      for (const watchtower of watchtowers) {
        updateWatchtowerCache(watchtower.watchtowerKey, watchtower);
      }

      return { watchtowers, nextPageToken };
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return {
    error,
    fetchNextPage,
    loaded: !hasNextPage,
    loading: isLoading,
    pages: data?.pages ?? [],
  };
}
